import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { getFilesByKnowledgeBaseId } from '@/features/knowledge-bases';
import { mapRagFileMetadataToFileRow } from '@/features/knowledge-bases/lib/file-row-mappers';
import type { UseKnowledgeBaseFilesParams } from './useKnowledgeBaseDetailFiles.types';

/**
 * Hook for fetching paginated files from a knowledge base
 * Focused responsibility: Knowledge base files data fetching with pagination
 */
export const useKnowledgeBaseDetailFiles = (params?: UseKnowledgeBaseFilesParams) => {
  const { knowledgeBaseId = null } = useParams<{ knowledgeBaseId: string }>();
  const {
    sortBy = 'created',
    sortOrder = 'desc',
    skip = 0,
    take = 10,
    search,
    enabled = true,
  } = params ?? {};

  const filesQuery = useQuery({
    queryKey: [
      ...KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
      'files',
      sortBy,
      sortOrder,
      skip,
      take,
      search,
    ],
    queryFn: () => {
      if (!knowledgeBaseId) {
        throw new Error('Knowledge base ID is required');
      }
      return getFilesByKnowledgeBaseId(knowledgeBaseId, {
        sortBy,
        sortOrder,
        skip,
        // Over-fetch by one so a full page can be told apart from a full-but-last
        // page: more than `take` back means there's a real next page, otherwise
        // this is the last page and the derived total is exact.
        take: take + 1,
        search,
      });
    },
    enabled: !!knowledgeBaseId && enabled,
    retry: false,
    retryOnMount: false,
  });

  const page = filesQuery.data ?? [];
  // A confirmed extra row (we asked for take + 1) means there's another page.
  const hasMore = page.length > take;
  const files = (hasMore ? page.slice(0, take) : page).map(mapRagFileMetadataToFileRow);

  // The endpoint returns a bare page with no filtered total, so derive one from
  // the over-fetched page: a confirmed extra row means at least one more page,
  // otherwise this page is the last and the total is exact. Callers with an
  // authoritative count (e.g. the unfiltered KB `fileCount`) should prefer it;
  // this is the best signal available when a `search` narrows the set.
  const estimatedTotalCount = skip + files.length + (hasMore ? 1 : 0);

  return {
    files,
    estimatedTotalCount,
    isLoading: filesQuery.isLoading,
    isFetching: filesQuery.isFetching,
    error: filesQuery.error,
    isError: filesQuery.isError,
    isSuccess: filesQuery.isSuccess,
    refetch: filesQuery.refetch,
  };
};
